import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { PageTitle, Panel } from "@/pages/admin/AdminLayout";
import { useAuth } from "@/pages/admin/AdminAuth";

const STATS = [
  ["in_stock", "Em stock", "/admin/relogios"], ["reserved", "Reservados", "/admin/relogios?estado=reserved"], ["on_order", "Por encomenda", "/admin/relogios"],
  ["drafts", "Rascunhos", "/admin/relogios?estado=draft"], ["sold_30d", "Vendidos (30 dias)", "/admin/relogios?estado=sold"], ["requests_unanswered", "Pedidos por responder", "/admin/pedidos?estado=new"],
  ["orders_open", "Pedidos em aberto", "/admin/pedidos"], ["held_units", "Unidades cativas", "/admin/stock"], ["supplier_ordered", "Encomendados ao fornecedor", "/admin/pedidos?estado=supplier_ordered"], ["conversations_7d", "Conversas do consultor (7 dias)", "/admin/consultor"],
];

function TopInterest() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/stats/interest?days=7").then((r) => setRows(r.data.ranking.slice(0, 5))).catch(() => setRows("error")); }, []);
  if (!rows) return null;
  return (
    <div data-testid="dashboard-top-interest">
      <div className="flex items-end justify-between gap-4 mt-12 mb-4"><h2 className="text-xl text-[#F3EFE6]">Modelos com mais interesse · 7 dias</h2><Link to="/admin/interesse" className="shrink-0 whitespace-nowrap text-xs text-[#C8AE6E] hover:underline" data-testid="dashboard-top-interest-more">Ver tudo</Link></div>
      {rows === "error" ? <p className="text-sm text-[#e08f7f]" data-testid="dashboard-top-interest-error">Não foi possível carregar o interesse nos modelos.</p> : !rows.length ? <p className="text-sm text-[#A39E93]" data-testid="dashboard-top-interest-empty">Ainda sem visitas esta semana.</p> : (
        <Panel className="!p-0 divide-y divide-[#282520]">
          {rows.map((x, i) => (
            <Link key={x.watch_id} to={`/admin/relogios/${x.watch_id}`} className="flex items-center gap-4 px-4 py-3 hover:bg-[#1c1a16] transition-colors duration-300" data-testid={`dashboard-top-${i + 1}`}>
              <span className="font-display text-2xl text-[#C8AE6E] w-6">{i + 1}</span>
              {x.thumb ? <img src={x.thumb} alt="" className="w-10 h-10 object-cover" /> : <span className="w-10 h-10 bg-[#282520]" />}
              <span className="flex-1 text-sm text-[#F3EFE6]">{x.label}</span>
              <span className="hidden sm:block text-xs text-[#A39E93] tabular-nums">{x.view} visitas · {x.whatsapp} WhatsApp · {x.request} pedidos</span>
              <span className="text-xs text-[#E6D5AC] tabular-nums text-right whitespace-nowrap" title="Pontuação de interesse">{x.score} pts</span>
            </Link>
          ))}
        </Panel>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then((r) => setD(r.data)); }, []);
  if (!d) return null;
  const isSuper = user?.role === "superadmin";
  return (
    <div data-testid="admin-dashboard">
      <PageTitle title="Resumo" sub={isSuper && d.ai_cost_month != null ? `Custo do consultor este mês: ${d.ai_cost_month.toFixed(2)} € de ${d.ai_cost_limit} €` : undefined} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {STATS.map(([k, l, to]) => (
          <Link key={k} to={to} className="bg-[#161512] border border-[#282520] hover:border-[#C8AE6E]/40 p-5 transition-colors duration-300" data-testid={`stat-${k}`}>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#A39E93]">{l}</p>
            <p className="font-display text-4xl text-[#E6D5AC] mt-3">{d[k]}</p>
          </Link>
        ))}
      </div>
      <TopInterest />
      <h2 className="text-xl mt-12 mb-4 text-[#F3EFE6]">Alertas</h2>
      {d.alerts.length === 0 ? <p className="text-sm text-[#A39E93]" data-testid="no-alerts">Sem alertas.</p> : (
        <div className="space-y-2" data-testid="alerts-list">
          {d.alerts.map((a, i) => (
            <Panel key={i} className="!p-4 flex items-center gap-3 text-sm" data-testid={`alert-${a.kind}`}>
              <AlertTriangle className="w-4 h-4 text-[#e0b97f] shrink-0" />
              {a.kind === "no_photos" && <span>Relógio visível sem fotografias: <Link className="text-[#E6D5AC] underline" to={`/admin/relogios/${a.watch_id}`}>{a.label}</Link></span>}
              {a.kind === "video_error" && <span>Vídeo com erro de conversão em <Link className="text-[#E6D5AC] underline" to={`/admin/relogios/${a.watch_id}`}>{a.label}</Link> — {a.detail}</span>}
              {a.kind === "hold_no_deposit" && <span className="flex-1 flex flex-wrap items-center gap-2">Reservado sem sinal pago{a.overdue ? ` há ${a.age_days} dia(s)` : ""}: <Link className="text-[#E6D5AC] underline" to={`/admin/pedidos/${a.request_id}`}>{a.label}</Link><span className="text-xs text-[#A39E93]">· {a.owner ? `responsável ${a.owner}` : "sem responsável"}</span>
                {a.owner_phone && <a href={`https://wa.me/${a.owner_phone.replace(/\D/g, "").replace(/^(9\d{8})$/, "351$1")}?text=${encodeURIComponent(`Lembrete GoldenTime: a reserva ${a.label} ainda não tem sinal pago (${a.age_days} dia(s)). ${window.location.origin}/admin/pedidos/${a.request_id}`)}`} target="_blank" rel="noopener noreferrer" className="ml-auto text-xs text-[#C8AE6E] underline" data-testid={`alert-remind-${a.request_id}`}>Lembrar por WhatsApp</a>}</span>}
              {a.kind === "waitlist_ready" && <span>Lista de espera — peça disponível, avisar cliente: <Link className="text-[#E6D5AC] underline" to={`/admin/pedidos/${a.request_id}`}>{a.label}</Link></span>}
              {a.kind === "hold_expired" && <span>Reserva com prazo expirado: <Link className="text-[#E6D5AC] underline" to={`/admin/pedidos/${a.request_id}`}>{a.label}</Link></span>}
              {a.kind === "ai_cost" && <span>Limite de custo do consultor próximo: {a.label}</span>}
            </Panel>
          ))}
        </div>
      )}
    </div>
  );
}
